import React, { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Camera, X, ZoomIn, ZoomOut, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const OUTPUT_SIZE = 512;

/**
 * Phase 3.5B — Avatar upload experience.
 *
 * Full-featured drop-in for the Profile tab:
 *   • Click-to-upload OR drag-and-drop
 *   • react-easy-crop for square crop + zoom + reposition
 *   • Saves the cropped canvas as WebP (falls back to JPEG) at 512×512
 *   • Uploads via POST /api/me/profile/avatar
 *   • Preview updates immediately from the response
 */
export default function AvatarUploader({
  currentUrl,
  onUploaded,           // (mediaId, url) => void
  onRemoved,            // () => void — Phase 3.5C
  testidPrefix = "avatar",
}) {
  const inputRef = useRef(null);
  const [srcDataUrl, setSrcDataUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleFile = (file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Please choose a JPEG, PNG, or WebP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image is too large. Maximum 10 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSrcDataUrl(reader.result);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    };
    reader.readAsDataURL(file);
  };

  const cancel = () => {
    setSrcDataUrl(null);
    setCroppedAreaPixels(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const save = async () => {
    if (!srcDataUrl || !croppedAreaPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(srcDataUrl, croppedAreaPixels);
      const fd = new FormData();
      fd.append("file", blob, "avatar.webp");
      const r = await api.post("/me/profile/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Avatar updated.");
      onUploaded?.(r.data.media_id, r.data.url);
      cancel();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  // -------- Cropping view --------
  if (srcDataUrl) {
    return (
      <div className="space-y-4" data-testid={`${testidPrefix}-cropper`}>
        <div className="relative w-full aspect-square max-w-xs mx-auto rounded-2xl overflow-hidden border-2 border-[var(--ink)] bg-black">
          <Cropper
            image={srcDataUrl}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="flex items-center gap-3 max-w-xs mx-auto">
          <ZoomOut size={16} className="text-[var(--smoke)] shrink-0" />
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-[var(--panini-red)]"
            data-testid={`${testidPrefix}-zoom`}
            aria-label="Zoom"
          />
          <ZoomIn size={16} className="text-[var(--smoke)] shrink-0" />
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid={`${testidPrefix}-save`}
            className="btn-red text-sm disabled:opacity-60"
          >
            {busy ? "Saving…" : "Save avatar"}
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            data-testid={`${testidPrefix}-cancel`}
            className="btn-ghost text-sm disabled:opacity-60"
          >
            <X size={14} /> Cancel
          </button>
        </div>
        <p className="text-[11px] text-[var(--smoke)] text-center">
          Drag to reposition · pinch or use the slider to zoom · 512 × 512 output
        </p>
      </div>
    );
  }

  // -------- Idle view: current avatar + drop zone --------
  return (
    <div className="flex flex-col sm:flex-row items-center gap-6" data-testid={`${testidPrefix}-uploader`}>
      <div className="relative shrink-0">
        <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] flex items-center justify-center shadow-[3px_3px_0_var(--ink)]">
          {currentUrl ? (
            <img
              src={currentUrl}
              alt="avatar"
              className="w-full h-full object-cover"
              data-testid={`${testidPrefix}-current`}
            />
          ) : (
            <Camera className="text-[var(--ink)]" size={28} />
          )}
        </div>
      </div>

      <label
        htmlFor={`${testidPrefix}-input`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid={`${testidPrefix}-drop`}
        className={`flex-1 w-full sm:w-auto flex flex-col items-center justify-center text-center gap-2 border-2 border-dashed rounded-2xl px-6 py-6 cursor-pointer transition ${
          dragOver
            ? "border-[var(--panini-red)] bg-[var(--panini-red-soft)]"
            : "border-[var(--ink)] bg-white hover:bg-[var(--panini-yellow-soft)]"
        }`}
      >
        <Upload size={22} className="text-[var(--ink)]" />
        <div className="text-sm font-bold text-[var(--ink)]">
          Click to upload <span className="font-normal text-[var(--smoke)]">or drag &amp; drop</span>
        </div>
        <div className="text-[11px] text-[var(--smoke)]">JPEG, PNG, or WebP · up to 10 MB</div>
        <input
          id={`${testidPrefix}-input`}
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
          data-testid={`${testidPrefix}-input`}
        />
      </label>
      {currentUrl && onRemoved && (
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm("Remove your profile photo?")) return;
            try {
              await api.delete("/me/profile/avatar");
              toast.success("Photo removed.");
              onRemoved?.();
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail) || e.message);
            }
          }}
          data-testid={`${testidPrefix}-remove`}
          className="btn-ghost text-xs shrink-0"
        >
          Remove
        </button>
      )}
    </div>
  );
}

/**
 * Crop the source image to the pixel area from react-easy-crop and export
 * as a 512×512 WebP blob (falls back to JPEG on older browsers).
 */
async function getCroppedBlob(dataUrl, area) {
  const image = await loadImage(dataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    area.x, area.y, area.width, area.height,
    0, 0, OUTPUT_SIZE, OUTPUT_SIZE,
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) return resolve(b);
        canvas.toBlob(
          (jpg) => (jpg ? resolve(jpg) : reject(new Error("Canvas export failed"))),
          "image/jpeg", 0.92,
        );
      },
      "image/webp", 0.92,
    );
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
